import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import SortHeader, { useSort, sortArr } from '../components/SortHeader';
import { joursFeriesFR } from '../lib/joursFeries';

const TYPE_LABELS = {
  cp: 'Congé payé',
  absence_np: 'Absence non payée',
  arret: 'Arrêt maladie',
  formation: 'Formation',
};
const TYPE_COLORS = {
  cp: 'bg-emerald-100 text-emerald-800 border-emerald-400',
  absence_np: 'bg-rose-100 text-rose-700 border-rose-300',
  arret: 'bg-amber-100 text-amber-800 border-amber-400',
  formation: 'bg-blue-100 text-[#002FA7] border-[#002FA7]',
};
const MONTHS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

function toIso(d) {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

export default function MesAbsences() {
  const { user } = useAuth();
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth());
  const [year, setYear] = useState(today.getFullYear());
  const [absences, setAbsences] = useState(null);
  const [openDay, setOpenDay] = useState(null); // { date, existing }
  const [form, setForm] = useState({ type: 'cp', commentaire: '' });
  const [sort, toggleSort] = useSort('date_debut', 'asc');

  const load = async () => {
    const { data } = await api.get('/absences');
    setAbsences(data);
  };
  useEffect(() => { load(); }, []);

  // Calendrier : lundi en première colonne
  const gridDays = useMemo(() => {
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const firstDow = first.getDay();
    const offset = firstDow === 0 ? -6 : 1 - firstDow;
    const start = new Date(year, month, 1 + offset);
    const lastDow = last.getDay();
    const endOffset = lastDow === 0 ? 0 : 7 - lastDow;
    const end = new Date(year, month, last.getDate() + endOffset);
    const days = [];
    const cur = new Date(start);
    while (cur <= end) { days.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
    return days;
  }, [month, year]);

  // Jours fériés couvrant éventuellement 2 années (quand la grille déborde)
  const joursFeries = useMemo(() => {
    const years = new Set(gridDays.map(d => d.getFullYear()));
    const merged = {};
    for (const y of years) Object.assign(merged, joursFeriesFR(y));
    return merged;
  }, [gridDays]);

  // Map date ISO -> absence (éclate les intervalles sur chaque jour)
  const absenceByDay = useMemo(() => {
    const m = {};
    for (const a of (absences || [])) {
      const s = new Date(a.date_debut);
      const e = new Date(a.date_fin || a.date_debut);
      const cur = new Date(s);
      while (cur <= e) {
        m[toIso(cur)] = a;
        cur.setDate(cur.getDate() + 1);
      }
    }
    return m;
  }, [absences]);

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1);
  };

  const clickDay = (d) => {
    const iso = toIso(d);
    const existing = absenceByDay[iso];
    setOpenDay({ date: iso, existing });
    setForm({ type: existing?.type || 'cp', commentaire: existing?.commentaire || '' });
  };

  const saveDay = async () => {
    if (!openDay) return;
    try {
      if (openDay.existing) {
        if (openDay.existing.date_debut !== openDay.existing.date_fin) {
          toast.info('Cette absence couvre plusieurs jours. Modifiez-la depuis la liste ci-dessous.');
          return;
        }
        await api.delete(`/absences/${openDay.existing.id}`);
      }
      await api.post('/absences', {
        type: form.type, date_debut: openDay.date, date_fin: openDay.date, commentaire: form.commentaire,
      });
      toast.success('Absence enregistrée');
      setOpenDay(null);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const deleteDay = async () => {
    if (!openDay?.existing) return;
    try {
      await api.delete(`/absences/${openDay.existing.id}`);
      toast.success('Supprimée');
      setOpenDay(null);
      await load();
    } catch (err) { toast.error('Erreur'); }
  };

  const removeAbsence = async (id) => {
    if (!window.confirm('Supprimer cette absence ?')) return;
    await api.delete(`/absences/${id}`);
    load();
  };

  const sortedAbsences = useMemo(() => sortArr((absences || []), sort, (a, k) => a[k]), [absences, sort]);
  const isLoaded = absences !== null;

  const todayIso = toIso(today);

  return (
    <Layout
      title="Mes absences"
      subtitle={`${user?.prenom} ${user?.nom}`}
      actions={
        <div className="flex items-center gap-2">
          <button onClick={prevMonth} className="p-2 border border-zinc-900 hover:bg-zinc-900 hover:text-white" data-testid="prev-month" title="Mois précédent"><ChevronLeft size={16} /></button>
          <div className="px-4 py-2 border border-zinc-900 font-mono text-xs bg-white min-w-[180px] text-center" data-testid="month-label">{MONTHS_FR[month]} {year}</div>
          <button onClick={nextMonth} className="p-2 border border-zinc-900 hover:bg-zinc-900 hover:text-white" data-testid="next-month" title="Mois suivant"><ChevronRight size={16} /></button>
          <button onClick={() => { setMonth(today.getMonth()); setYear(today.getFullYear()); }}
            className="px-3 py-2 border border-zinc-900 hover:bg-zinc-900 hover:text-white text-xs uppercase tracking-wider"
            data-testid="this-month">Ce mois</button>
        </div>
      }
    >
      {/* Calendrier mensuel */}
      <div className="bg-white border border-zinc-200" data-testid="mes-absences-calendar">
        <div className="grid grid-cols-7 border-b-2 border-zinc-900 bg-zinc-50">
          {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((d) => (
            <div key={d} className={`px-3 py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-700 font-medium border-r border-zinc-200 text-center ${d === 'Sam' || d === 'Dim' ? 'bg-zinc-100' : ''}`}>{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {gridDays.map((d, i) => {
            const iso = toIso(d);
            const isCurrentMonth = d.getMonth() === month;
            const isWeekend = d.getDay() === 0 || d.getDay() === 6;
            const abs = absenceByDay[iso];
            const jourFerie = joursFeries[iso];
            const isToday = iso === todayIso;
            return (
              <button key={i} onClick={() => isCurrentMonth && clickDay(d)}
                disabled={!isCurrentMonth}
                className={`relative min-h-[92px] p-2 border-b border-r border-zinc-100 text-left transition-colors
                  ${!isCurrentMonth ? 'bg-zinc-50/50 text-zinc-300 cursor-default' : 'hover:bg-blue-50/40 cursor-pointer'}
                  ${isWeekend && isCurrentMonth && !jourFerie ? 'bg-zinc-50/60' : ''}
                  ${jourFerie && isCurrentMonth ? 'bg-rose-50/60' : ''}
                  ${isToday ? 'ring-2 ring-inset ring-[#002FA7]' : ''}
                `}
                data-testid={isCurrentMonth ? `day-${iso}` : undefined}
                title={jourFerie ? `${jourFerie} (férié)` : ''}
              >
                <div className={`flex items-start justify-between`}>
                  <div className={`text-xs font-mono ${isCurrentMonth ? 'text-zinc-700' : ''} ${isToday ? 'text-[#002FA7] font-bold' : ''}`}>
                    {String(d.getDate()).padStart(2, '0')}
                  </div>
                  {jourFerie && isCurrentMonth && (
                    <span className="text-[8px] uppercase tracking-wider font-semibold text-rose-700 bg-rose-100 px-1 py-0.5 border border-rose-300" data-testid={`ferie-${iso}`}>Férié</span>
                  )}
                </div>
                {jourFerie && isCurrentMonth && (
                  <div className="text-[9px] text-rose-700 leading-tight mt-0.5 italic">{jourFerie}</div>
                )}
                {abs && isCurrentMonth && (
                  <div className={`mt-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider border ${TYPE_COLORS[abs.type]}`} data-testid={`abs-${iso}`}>
                    {TYPE_LABELS[abs.type]}
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <div className="border-t border-zinc-200 px-4 py-2 flex flex-wrap gap-3 text-[10px] bg-zinc-50/50">
          {Object.entries(TYPE_LABELS).map(([k, v]) => (
            <span key={k} className={`inline-flex items-center gap-1.5`}>
              <span className={`inline-block w-3 h-3 border ${TYPE_COLORS[k]}`}></span>
              <span className="text-zinc-600">{v}</span>
            </span>
          ))}
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 bg-rose-50 border border-rose-300"></span>
            <span className="text-zinc-600">Jour férié</span>
          </span>
          <span className="ml-auto text-zinc-500 italic">Cliquez sur un jour pour ajouter/modifier une absence</span>
        </div>
      </div>

      {/* Liste triable */}
      <div className="mt-6 bg-white border border-zinc-200 overflow-x-auto" data-testid="mes-absences-list">
        <div className="border-b border-zinc-900 px-4 py-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 font-medium">Toutes mes absences ({(absences || []).length})</div>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-200 text-left">
              <SortHeader sort={sort} onToggle={toggleSort} k="type" label="Type" />
              <SortHeader sort={sort} onToggle={toggleSort} k="date_debut" label="Début" />
              <SortHeader sort={sort} onToggle={toggleSort} k="date_fin" label="Fin" />
              <th className="px-4 py-2 text-left text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Commentaire</th>
              <th className="px-4 py-2 w-[50px]"></th>
            </tr>
          </thead>
          <tbody>
            {sortedAbsences.map((a) => (
              <tr key={a.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                <td className="px-4 py-2">
                  <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-medium border ${TYPE_COLORS[a.type]}`}>
                    {TYPE_LABELS[a.type]}
                  </span>
                </td>
                <td className="px-4 py-2 font-mono text-xs">{a.date_debut}</td>
                <td className="px-4 py-2 font-mono text-xs">{a.date_fin}</td>
                <td className="px-4 py-2 text-xs text-zinc-600">{a.commentaire || '—'}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => removeAbsence(a.id)} className="p-1 hover:bg-rose-100 text-rose-600"><Trash2 size={12} /></button>
                </td>
              </tr>
            ))}
            {(absences || []).length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-500 text-sm">{isLoaded ? 'Aucune absence.' : 'Chargement…'}</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!openDay} onOpenChange={(o) => !o && setOpenDay(null)}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle className="font-display font-bold text-xl">
              {openDay?.existing ? 'Modifier' : 'Nouvelle'} absence — <span className="font-mono text-base">{openDay?.date}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Type *</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="rounded-none" data-testid="absence-type"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Commentaire</Label>
              <Input className="rounded-none" value={form.commentaire} onChange={(e) => setForm({ ...form, commentaire: e.target.value })} />
            </div>
            <div className="flex gap-2 pt-2">
              {openDay?.existing ? (
                <>
                  <Button type="button" onClick={saveDay} className="flex-1 rounded-none bg-[#002FA7] hover:bg-[#001B63]" data-testid="absence-save">Mettre à jour</Button>
                  <Button type="button" onClick={deleteDay} className="rounded-none bg-rose-600 hover:bg-rose-700 text-white" data-testid="absence-delete">Supprimer</Button>
                </>
              ) : (
                <Button type="button" onClick={saveDay} className="flex-1 rounded-none bg-[#002FA7] hover:bg-[#001B63]" data-testid="absence-save">Enregistrer</Button>
              )}
              <Button type="button" variant="outline" onClick={() => setOpenDay(null)} className="rounded-none border-zinc-900">Annuler</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
